import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select,
  MenuItem, Chip, Tooltip, CircularProgress, Snackbar, Alert, Tabs, Tab,
  LinearProgress, InputAdornment, Card, CardContent, Switch, FormControlLabel,
  Checkbox, alpha, Divider,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon,
  Search as SearchIcon, Inventory as InventoryIcon, Warning as WarningIcon,
  ShoppingCart as ShoppingCartIcon, Category as CategoryIcon,
  MedicalServices as MedIcon, Build as MaterialIcon, LocalPharmacy as PharmacyIcon,
  Close as CloseIcon, FilterList as FilterIcon, FileDownload as ExportIcon,
  Circle as CircleIcon, ArrowDownward as LowStockIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';

import { useLocation, useNavigate } from 'react-router-dom';

const ITEM_TYPES = [
  { value: '', label: 'Geral (Todos)' },
  { value: 'produto', label: 'Produto' },
  { value: 'material', label: 'Material' },
  { value: 'medicamento', label: 'Medicamento' },
];

const UNITS = [
  { value: 'unidade', label: 'Unidade' }, { value: 'caixa', label: 'Caixa' },
  { value: 'pacote', label: 'Pacote' }, { value: 'litro', label: 'Litro' },
  { value: 'kg', label: 'Kg' }, { value: 'ml', label: 'ml' },
  { value: 'mg', label: 'mg' }, { value: 'g', label: 'g' },
  { value: 'par', label: 'Par' }, { value: 'rolo', label: 'Rolo' },
  { value: 'frasco', label: 'Frasco' }, { value: 'ampola', label: 'Ampola' },
  { value: 'comprimido', label: 'Comprimido' }, { value: 'conjunto', label: 'Conjunto' },
];

const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';

const emptyForm = {
  name: '', description: '', item_type: 'geral', sku: '', barcode: '', unit: 'unidade',
  current_stock: 0, minimum_stock: 0, maximum_stock: 100,
  cost_price: 0, selling_price: 0, supplier_id: '', manufacturer_id: '',
  category_id: '', location_id: '', expiration_date: '', lot_number: '',
  storage_location: '', active_ingredient: '', concentration: '', presentation: '',
  anvisa_code: '', notes: '',
  content_quantity: '', expiry_warning_days: 30, allow_direct_exit: false,
};

const InventoryMain: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const initialType = params.get('type') || '';

  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(initialType);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [manufacturerFilter, setManufacturerFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [belowMinFilter, setBelowMinFilter] = useState('');
  const [orderBy, setOrderBy] = useState('name');

  const [categories, setCategories] = useState<any[]>([]);
  const [manufacturers, setManufacturers] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const tabIndex = ITEM_TYPES.findIndex(t => t.value === typeFilter);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const p: Record<string, any> = { page: page + 1, limit: rowsPerPage, order_by: orderBy };
      if (search) p.search = search;
      if (typeFilter) p.item_type = typeFilter;
      if (categoryFilter) p.category_id = categoryFilter;
      if (manufacturerFilter) p.manufacturer_id = manufacturerFilter;
      if (locationFilter) p.location_id = locationFilter;
      if (belowMinFilter) p.below_minimum = belowMinFilter;
      const res = await axios.get(getApiUrl('/inventory-module/items'), { params: p });
      setItems(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [page, rowsPerPage, search, typeFilter, categoryFilter, manufacturerFilter, locationFilter, belowMinFilter, orderBy]);

  const fetchMeta = useCallback(async () => {
    try {
      const [catRes, manRes, locRes, statRes] = await Promise.all([
        axios.get(getApiUrl('/inventory-module/categories')),
        axios.get(getApiUrl('/inventory-module/manufacturers')),
        axios.get(getApiUrl('/inventory-module/locations')),
        axios.get(getApiUrl('/inventory-module/dashboard')),
      ]);
      setCategories(catRes.data.data || []);
      setManufacturers(manRes.data.data || []);
      setLocations(locRes.data.data || []);
      setStats(statRes.data.data || null);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const t = p.get('type') || '';
    if (t !== typeFilter) setTypeFilter(t);
  }, [location.search]);

  const handleTabChange = (_: React.SyntheticEvent, idx: number) => {
    const val = ITEM_TYPES[idx].value;
    setTypeFilter(val);
    setPage(0);
    navigate(val ? `/inventory/items?type=${val}` : '/inventory/items');
  };

  const openDialog = (item?: any) => {
    if (item) {
      setEditingId(item.id);
      setForm({
        ...emptyForm,
        name: item.name || '', description: item.description || '', item_type: item.item_type || 'geral',
        sku: item.sku || '', barcode: item.barcode || '', unit: item.unit || 'unidade',
        current_stock: item.current_stock ?? 0, minimum_stock: item.minimum_stock ?? 0, maximum_stock: item.maximum_stock ?? 100,
        cost_price: item.cost_price ?? 0, selling_price: item.selling_price ?? 0,
        supplier_id: item.supplier_id || '', manufacturer_id: item.manufacturer_id || '',
        category_id: item.category_id || '', location_id: item.location_id || '',
        expiration_date: item.expiration_date ? String(item.expiration_date).substring(0, 10) : '',
        lot_number: item.lot_number || '', storage_location: item.storage_location || '',
        active_ingredient: item.active_ingredient || '', concentration: item.concentration || '',
        presentation: item.presentation || '', anvisa_code: item.anvisa_code || '',
        notes: item.notes || '',
        content_quantity: item.metadata?.content_quantity || '',
        expiry_warning_days: item.metadata?.expiry_warning_days ?? 30,
        allow_direct_exit: item.metadata?.allow_direct_exit ?? false,
      });
    } else {
      setEditingId(null);
      setForm({ ...emptyForm, item_type: typeFilter || 'geral' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setSnackbar({ open: true, message: 'Nome obrigatório', severity: 'error' }); return; }
    try {
      setSaving(true);
      const payload: any = {
        name: form.name, description: form.description, item_type: form.item_type,
        sku: form.sku, barcode: form.barcode, unit: form.unit,
        current_stock: Number(form.current_stock), minimum_stock: Number(form.minimum_stock),
        maximum_stock: Number(form.maximum_stock), cost_price: Number(form.cost_price),
        selling_price: Number(form.selling_price),
        category_id: form.category_id || null, manufacturer_id: form.manufacturer_id || null,
        location_id: form.location_id || null, supplier_id: form.supplier_id || null,
        expiration_date: form.expiration_date || null, lot_number: form.lot_number,
        storage_location: form.storage_location, notes: form.notes,
        active_ingredient: form.active_ingredient, concentration: form.concentration,
        presentation: form.presentation, anvisa_code: form.anvisa_code,
        metadata: {
          content_quantity: form.content_quantity,
          expiry_warning_days: Number(form.expiry_warning_days) || 30,
          allow_direct_exit: form.allow_direct_exit,
        },
      };
      if (editingId) {
        await axios.put(getApiUrl(`/inventory-module/items/${editingId}`), payload);
        setSnackbar({ open: true, message: 'Item atualizado!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/inventory-module/items'), payload);
        setSnackbar({ open: true, message: 'Item cadastrado!', severity: 'success' });
      }
      setDialogOpen(false);
      fetchItems(); fetchMeta();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao salvar', severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja desativar este item?')) return;
    try {
      await axios.delete(getApiUrl(`/inventory-module/items/${id}`));
      setSnackbar({ open: true, message: 'Item desativado!', severity: 'success' });
      fetchItems(); fetchMeta();
    } catch { setSnackbar({ open: true, message: 'Erro ao excluir', severity: 'error' }); }
  };

  const getStockColor = (item: any) => {
    if (Number(item.current_stock) <= Number(item.minimum_stock)) return '#ef4444';
    if (Number(item.current_stock) <= Number(item.minimum_stock) * 1.5) return '#f59e0b';
    return '#10b981';
  };

  const getStockLevel = (item: any) => {
    if (!item.maximum_stock || Number(item.maximum_stock) === 0) return 50;
    return Math.min(100, (Number(item.current_stock) / Number(item.maximum_stock)) * 100);
  };

  const isExpired = (d: string) => d && new Date(d) < new Date();
  const isExpiringSoon = (d: string) => {
    if (!d) return false;
    const exp = new Date(d);
    const in30 = new Date(); in30.setDate(in30.getDate() + 30);
    return exp >= new Date() && exp <= in30;
  };

  const handleExportCSV = () => {
    if (items.length === 0) return;
    const headers = ['Produto', 'Código', 'Tipo', 'Categoria', 'Fabricante', 'Localização', 'Estoque', 'Unidade', 'Mínimo', 'Máximo', 'Custo', 'Venda', 'Lote', 'Validade'];
    const rows = items.map(i => [
      i.name, i.sku || i.barcode || '', i.item_type, i.clinic_inventory_categories?.name || '',
      i.clinic_inventory_manufacturers?.name || '', i.clinic_inventory_locations?.name || i.storage_location || '',
      i.current_stock, i.unit, i.minimum_stock, i.maximum_stock, i.cost_price, i.selling_price,
      i.lot_number || '', i.expiration_date ? formatDate(i.expiration_date) : '',
    ]);
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `estoque_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  return (
    <Box sx={pageContainerSx}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: { xs: 2, md: 2 },
        gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <InventoryIcon sx={{ fontSize: 32, color: '#8b5cf6' }} />
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, lineHeight: 1.2 }}>Estoque</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button variant="outlined" fullWidth={isMobile} startIcon={<ExportIcon />} onClick={handleExportCSV} disabled={items.length === 0}
            sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#8b5cf6', color: '#8b5cf6' } }}>
            Exportar
          </Button>
          <Button variant="contained" fullWidth={isMobile} startIcon={<AddIcon />} onClick={() => openDialog()}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
            Novo Item
          </Button>
        </Box>
      </Box>

      {/* KPI Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total de Itens', value: stats.total_items, icon: <InventoryIcon />, color: '#8b5cf6', bg: '#faf5ff' },
            { label: 'Produtos', value: stats.total_products, icon: <ShoppingCartIcon />, color: '#3b82f6', bg: '#eff6ff' },
            { label: 'Materiais', value: stats.total_materials, icon: <MaterialIcon />, color: '#f59e0b', bg: '#fffbeb' },
            { label: 'Medicamentos', value: stats.total_medications, icon: <PharmacyIcon />, color: '#10b981', bg: '#f0fdf4' },
            { label: 'Estoque Baixo', value: stats.low_stock, icon: <WarningIcon />, color: '#ef4444', bg: '#fef2f2' },
            { label: 'Vencidos', value: stats.expired, icon: <WarningIcon />, color: '#dc2626', bg: '#fef2f2' },
            { label: 'Vencendo em 30d', value: stats.expiring_soon, icon: <WarningIcon />, color: '#f59e0b', bg: '#fffbeb' },
            { label: 'Valor em Estoque', value: formatCurrency(stats.total_value), icon: <ShoppingCartIcon />, color: '#059669', bg: '#f0fdf4', isText: true },
          ].map((kpi, i) => (
            <Grid item xs={6} sm={4} md={3} lg={1.5} key={i}>
              <Card sx={{ borderRadius: 2, border: `1px solid ${kpi.color}22`, bgcolor: kpi.bg, cursor: kpi.label === 'Estoque Baixo' ? 'pointer' : 'default', transition: 'all 0.2s', '&:hover': { transform: kpi.label === 'Estoque Baixo' ? 'translateY(-2px)' : 'none', boxShadow: kpi.label === 'Estoque Baixo' ? '0 4px 12px rgba(0,0,0,0.08)' : 'none' } }}
                onClick={() => { if (kpi.label === 'Estoque Baixo') { setBelowMinFilter(belowMinFilter ? '' : 'true'); setPage(0); } }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Box sx={{ color: kpi.color, '& svg': { fontSize: 18 } }}>{kpi.icon}</Box>
                    <Typography variant="caption" color="text.secondary" noWrap>{kpi.label}</Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: kpi.color, fontSize: kpi.isText ? '0.85rem' : '1.2rem' }}>
                    {kpi.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Tabs por tipo */}
      <Paper sx={{ borderRadius: 2, mb: 2 }}>
        <Tabs value={tabIndex >= 0 ? tabIndex : 0} onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
          <Tab icon={<InventoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Geral" />
          <Tab icon={<ShoppingCartIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Produto" />
          <Tab icon={<MaterialIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Material" />
          <Tab icon={<PharmacyIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Medicamento" />
        </Tabs>

        {/* Filtros */}
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField size="small" fullWidth placeholder="Buscar produto..." value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                onKeyDown={(e) => e.key === 'Enter' && fetchItems()}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#9ca3af' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Categoria</InputLabel>
                <Select value={categoryFilter} label="Categoria" onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}>
                  <MenuItem value="">Todas</MenuItem>
                  {categories.filter(c => c.is_active).map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Fabricante</InputLabel>
                <Select value={manufacturerFilter} label="Fabricante" onChange={(e) => { setManufacturerFilter(e.target.value); setPage(0); }}>
                  <MenuItem value="">Todos</MenuItem>
                  {manufacturers.filter(m => m.is_active).map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Localização</InputLabel>
                <Select value={locationFilter} label="Localização" onChange={(e) => { setLocationFilter(e.target.value); setPage(0); }}>
                  <MenuItem value="">Todas</MenuItem>
                  {locations.filter(l => l.is_active).map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={1.5}>
              <FormControl size="small" fullWidth>
                <InputLabel>Ordenar</InputLabel>
                <Select value={orderBy} label="Ordenar" onChange={(e) => setOrderBy(e.target.value)}>
                  <MenuItem value="name">Nome</MenuItem>
                  <MenuItem value="current_stock">Estoque</MenuItem>
                  <MenuItem value="expiration_date">Validade</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={1.5} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title={belowMinFilter ? 'Mostrando: abaixo do mínimo — clique para limpar' : 'Filtrar abaixo do mínimo'}>
                <IconButton onClick={() => { setBelowMinFilter(belowMinFilter ? '' : 'true'); setPage(0); }}
                  sx={{ bgcolor: belowMinFilter ? '#fef2f2' : 'transparent', color: belowMinFilter ? '#ef4444' : '#9ca3af', border: belowMinFilter ? '1px solid #fecaca' : '1px solid transparent' }}>
                  <LowStockIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Buscar"><IconButton onClick={fetchItems}><SearchIcon /></IconButton></Tooltip>
              <Tooltip title="Atualizar"><IconButton onClick={() => { fetchItems(); fetchMeta(); }}><RefreshIcon /></IconButton></Tooltip>
            </Grid>
          </Grid>
        </Box>

        {/* Legenda de validade */}
        <Box sx={{ px: 2, pb: 1.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>Legenda:</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CircleIcon sx={{ fontSize: 10, color: '#dc2626' }} />
            <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 600 }}>Fora da validade</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CircleIcon sx={{ fontSize: 10, color: '#ea580c' }} />
            <Typography variant="caption" sx={{ color: '#ea580c', fontWeight: 600 }}>Próximo do vencimento</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CircleIcon sx={{ fontSize: 10, color: '#059669' }} />
            <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600 }}>Dentro do prazo</Typography>
          </Box>
          {belowMinFilter && (
            <Chip label="Abaixo do mínimo" size="small" onDelete={() => { setBelowMinFilter(''); setPage(0); }}
              sx={{ bgcolor: '#fef2f2', color: '#ef4444', fontWeight: 600, fontSize: '0.7rem', '& .MuiChip-deleteIcon': { color: '#ef4444' } }} />
          )}
        </Box>

        {/* Tabela */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#8b5cf6' }} /></Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f9fafb' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Produto</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Categoria</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Fabricante</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Localização</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Estoque</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 90 }}>Nível</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Validade</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#9ca3af' }}>
                        <InventoryIcon sx={{ fontSize: 36, opacity: 0.5, mb: 1 }} />
                        <Typography>Nenhum item encontrado</Typography>
                      </TableCell>
                    </TableRow>
                  ) : items.map((item) => (
                    <TableRow key={item.id} hover sx={{ bgcolor: Number(item.current_stock) <= Number(item.minimum_stock) ? '#fef2f2' : 'inherit' }}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.name}</Typography>
                          {item.active_ingredient && <Typography variant="caption" color="text.secondary">{item.active_ingredient} {item.concentration || ''}</Typography>}
                          {item.presentation && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{item.presentation}</Typography>}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>{item.sku || item.barcode || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={item.clinic_inventory_categories?.name || item.category || '-'}
                          size="small" sx={{ bgcolor: '#f3e8ff', color: '#7c3aed', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.clinic_inventory_manufacturers?.name || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.clinic_inventory_locations?.name || item.storage_location || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {item.current_stock} {item.unit}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9ca3af' }}>Min: {item.minimum_stock}</Typography>
                      </TableCell>
                      <TableCell>
                        <LinearProgress variant="determinate" value={getStockLevel(item)}
                          sx={{ height: 6, borderRadius: 3, bgcolor: '#e5e7eb', '& .MuiLinearProgress-bar': { bgcolor: getStockColor(item), borderRadius: 3 } }} />
                      </TableCell>
                      <TableCell>
                        {item.expiration_date ? (
                          <Chip
                            label={formatDate(item.expiration_date)}
                            size="small"
                            sx={{
                              fontWeight: 600, fontSize: '0.7rem',
                              bgcolor: isExpired(item.expiration_date) ? '#fecaca' : isExpiringSoon(item.expiration_date) ? '#fed7aa' : '#d1fae5',
                              color: isExpired(item.expiration_date) ? '#dc2626' : isExpiringSoon(item.expiration_date) ? '#ea580c' : '#059669',
                            }}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => openDialog(item)}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                        <Tooltip title="Desativar"><IconButton size="small" onClick={() => handleDelete(item.id)}><DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage}
              onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Por página" />
          </>
        )}
      </Paper>

      {/* Dialog Cadastro / Edição */}
      <Dialog fullScreen={isMobile} scroll="paper" open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
          {editingId ? 'Editar Item' : 'Novo Item'}
          <IconButton onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Nome *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small"><InputLabel>Tipo</InputLabel>
                <Select value={form.item_type} label="Tipo" onChange={(e) => setForm({ ...form, item_type: e.target.value })}>
                  <MenuItem value="geral">Geral</MenuItem>
                  <MenuItem value="produto">Produto</MenuItem>
                  <MenuItem value="material">Material</MenuItem>
                  <MenuItem value="medicamento">Medicamento</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small"><InputLabel>Categoria</InputLabel>
                <Select value={form.category_id} label="Categoria" onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                  <MenuItem value="">Nenhuma</MenuItem>
                  {categories.filter(c => c.is_active).map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Apresentação" value={form.presentation} onChange={(e) => setForm({ ...form, presentation: e.target.value })} size="small" placeholder="Ex: Cx c/ 30 comprimidos" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Conteúdo / Qtd por Embalagem" value={form.content_quantity} onChange={(e) => setForm({ ...form, content_quantity: e.target.value })} size="small" placeholder="Ex: 100" /></Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small"><InputLabel>Unidade</InputLabel>
                <Select value={form.unit} label="Unidade" onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                  {UNITS.map(u => <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}><TextField fullWidth label="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} size="small" multiline rows={2} /></Grid>

            <Grid item xs={12} sm={4}><TextField fullWidth label="SKU / Código" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Código de Barras" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small"><InputLabel>Fabricante</InputLabel>
                <Select value={form.manufacturer_id} label="Fabricante" onChange={(e) => setForm({ ...form, manufacturer_id: e.target.value })}>
                  <MenuItem value="">Nenhum</MenuItem>
                  {manufacturers.filter(m => m.is_active).map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small"><InputLabel>Localização Padrão</InputLabel>
                <Select value={form.location_id} label="Localização Padrão" onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
                  <MenuItem value="">Nenhuma</MenuItem>
                  {locations.filter(l => l.is_active).map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Lote" value={form.lot_number} onChange={(e) => setForm({ ...form, lot_number: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Validade" type="date" value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} size="small" InputLabelProps={{ shrink: true }} /></Grid>

            <Grid item xs={12}><Divider sx={{ my: 0.5 }}><Chip label="Estoque e Preços" size="small" sx={{ fontWeight: 600 }} /></Divider></Grid>

            <Grid item xs={12} sm={4}><TextField fullWidth label="Estoque Atual" type="number" value={form.current_stock} onChange={(e) => setForm({ ...form, current_stock: Number(e.target.value) })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Estoque Mínimo" type="number" value={form.minimum_stock} onChange={(e) => setForm({ ...form, minimum_stock: Number(e.target.value) })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Estoque Máximo" type="number" value={form.maximum_stock} onChange={(e) => setForm({ ...form, maximum_stock: Number(e.target.value) })} size="small" /></Grid>

            <Grid item xs={12} sm={3}><TextField fullWidth label="Preço Médio - Compra" type="number" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: Number(e.target.value) })} size="small" InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} /></Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth label="Preço Médio - Venda" type="number" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: Number(e.target.value) })} size="small" InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} /></Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth label="Aviso de Validade (dias)" type="number" value={form.expiry_warning_days} onChange={(e) => setForm({ ...form, expiry_warning_days: Number(e.target.value) })} size="small" helperText="Dias antes do vencimento" /></Grid>
            <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={<Checkbox checked={form.allow_direct_exit} onChange={(e) => setForm({ ...form, allow_direct_exit: e.target.checked })} />}
                label={<Typography variant="body2">Permitir saída pelo cadastro</Typography>}
              />
            </Grid>

            {(form.item_type === 'medicamento') && (
              <>
                <Grid item xs={12}><Divider sx={{ my: 0.5 }}><Chip label="Dados do Medicamento" size="small" sx={{ fontWeight: 600, bgcolor: '#f3e8ff', color: '#7c3aed' }} /></Divider></Grid>
                <Grid item xs={12} sm={3}><TextField fullWidth label="Princípio Ativo" value={form.active_ingredient} onChange={(e) => setForm({ ...form, active_ingredient: e.target.value })} size="small" /></Grid>
                <Grid item xs={12} sm={3}><TextField fullWidth label="Concentração" value={form.concentration} onChange={(e) => setForm({ ...form, concentration: e.target.value })} size="small" placeholder="Ex: 500mg" /></Grid>
                <Grid item xs={12} sm={3}><TextField fullWidth label="Código ANVISA" value={form.anvisa_code} onChange={(e) => setForm({ ...form, anvisa_code: e.target.value })} size="small" /></Grid>
                <Grid item xs={12} sm={3}><TextField fullWidth label="Armazenamento" value={form.storage_location} onChange={(e) => setForm({ ...form, storage_location: e.target.value })} size="small" placeholder="Ex: Refrigerado" /></Grid>
              </>
            )}

            <Grid item xs={12}><TextField fullWidth label="Observações" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} size="small" multiline rows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600 }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default InventoryMain;
